import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams, useLocation } from "react-router-dom";
import { api, fmtUSD } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Clock, ArrowRight, ArrowUpRight, Target, FileText, Link2, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import { Seo, organizationSchema } from "@/components/Seo";

const FALLBACK_CALENDLY = "https://calendly.com/swiles-sideb/30min";
const BOOK_NOW_SKUS = new Set(["discovery-session", "revenue-diagnostic", "revenue-audit-lite"]);
const CASE_STUDY_BY_SKU = {
  "rfp-project": { href: "/proof/rxp", label: "View RXP case study" },
  "aiops-enablement": { href: "/proof/aiops", label: "View AIOps case study" },
  "studio-retainer": { href: "/proof/studio-retainer", label: "View Studio Retainer case study" },
  "revenue-performance": { href: "/proof/revenue-performance", label: "View Revenue Performance case study" },
};

// Stripe Agentic Commerce / SEO: each category's terms+refund clause anchor.
// Mirrors the backend seed in server.py so deep-links stay in sync.
const REFUND_ANCHOR_BY_CATEGORY = {
  session: "#refund-sessions",
  package: "#refund-packages",
  retainer: "#refund-retainer",
  project: "#refund-custom",
};
const termsHrefFor = (s) =>
  s.refund_policy_url || `/terms${REFUND_ANCHOR_BY_CATEGORY[s.category] || "#refunds"}`;

const priceLabel = () => "Starting at";
const ctaLabel = (sku) => (BOOK_NOW_SKUS.has(sku) ? "Book now" : "Schedule discovery");
const intakeTag = (sku) =>
  BOOK_NOW_SKUS.has(sku) ? "Intake + scoping + payment" : "Intake + scoping";

const renderPrice = (s) => {
  if (s.quote_only) return <span className="text-2xl">Quote</span>;
  if (s.price === 0) return "Free";
  return fmtUSD(s.price);
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [highlighted, setHighlighted] = useState(null);
  const { sku: routeSku } = useParams();
  const location = useLocation();

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  // Deep-link support: /services/:sku and /services#:sku both scroll-and-pulse the target card.
  useEffect(() => {
    if (!services.length) return;
    const target = routeSku || (location.hash ? location.hash.slice(1) : null);
    if (!target) return;
    const el = document.getElementById(`engagement-${target}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlighted(target);
      const t = setTimeout(() => setHighlighted(null), 2400);
      return () => clearTimeout(t);
    }
  }, [services, routeSku, location.hash]);

  const handleBookClick = (s) => {
    track("service_book_clicked", {
      service_sku: s.sku,
      service_title: s.title,
      price: s.price,
      category: s.category,
    });
  };

  const copyShareLink = async (sku, title) => {
    const url = `${window.location.origin}/services/${sku}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: `${title} → ${url}` });
      track("service_link_copied", { service_sku: sku });
    } catch {
      toast.error("Couldn't copy — long-press the URL bar to share.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="services-page">
      <Seo
        title="Services · SideB — AIOps Partner & Process Automation Consulting"
        description="Productized AI-first RevOps consulting services from SideB. AIOps Partner retainers, payment gateway optimization, process automation consulting, and vendor-agnostic execution playbooks — scale operations without adding headcount."
        keywords="AIOps Partner, AI-first RevOps consulting, Process Automation Consulting, Process Improvement Consultant, Payment gateway optimization, Local payment method strategy, Operating Partner, Reduce SaaS bloat with internal AI"
        canonical="https://sideb.io/services"
        schema={organizationSchema()}
      />
      {services.length > 0 && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "SideB Consulting Studio — Services Catalog",
            "url": "https://sideb.io/services",
            "itemListElement": services.map((s, idx) => ({
              "@type": "ListItem",
              "position": idx + 1,
              "item": {
                "@type": "Service",
                "@id": `https://sideb.io/services/${s.sku}`,
                "name": s.title,
                "description": s.description,
                "serviceType": s.category,
                "provider": {
                  "@type": "Organization",
                  "name": "SideB Consulting Studio",
                  "url": "https://sideb.io",
                },
                "termsOfService": s.terms_url || "https://sideb.io/terms",
                "offers": s.quote_only
                  ? {
                      "@type": "Offer",
                      "availability": "https://schema.org/InStock",
                      "priceSpecification": { "@type": "PriceSpecification", "priceCurrency": "USD" },
                      "businessFunction": "https://schema.org/Sell",
                      "url": `https://sideb.io/services/${s.sku}`,
                      "termsOfService": s.terms_url || "https://sideb.io/terms",
                      "eligibleRegion": "Worldwide",
                    }
                  : {
                      "@type": "Offer",
                      "availability": "https://schema.org/InStock",
                      "price": s.price,
                      "priceCurrency": (s.currency || "usd").toUpperCase(),
                      "priceSpecification": {
                        "@type": s.recurring ? "UnitPriceSpecification" : "PriceSpecification",
                        "price": s.price,
                        "priceCurrency": (s.currency || "usd").toUpperCase(),
                        ...(s.recurring && {
                          "billingDuration": "P1M",
                          "billingIncrement": 1,
                          "unitCode": "MON",
                          "referenceQuantity": { "@type": "QuantitativeValue", "value": 1, "unitCode": "MON" },
                        }),
                      },
                      "url": `https://sideb.io/services/${s.sku}`,
                      "termsOfService": s.terms_url || "https://sideb.io/terms",
                      "eligibleRegion": "Worldwide",
                    },
              },
            })),
          })}</script>
        </Helmet>
      )}
      <ExitIntentPopup />
      <div className="label-mono">Engagements</div>
      <h1 className="font-display text-5xl sm:text-6xl font-semibold mt-3 tracking-[-0.04em]">Services</h1>
      <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">
        Every engagement is fixed-scope and built around a clear deliverable. Pick the one that matches where you are, then schedule on the calendar. Intake, scheduling and payment all happen in one place.
      </p>

      <div className="mt-16 grid md:grid-cols-2 gap-6">
        {services.map((s, i) => {
          const isRetainer = s.category === "retainer";
          const isFree = s.price === 0;
          const bookingUrl = s.google_calendar_url || FALLBACK_CALENDLY;
          return (
            <div
              key={s.id}
              id={`engagement-${s.sku}`}
              className={`card-sharp p-8 flex flex-col scroll-mt-24 transition-shadow duration-500 ${
                isRetainer ? "border-[#ac62ea]/40 bg-gradient-to-br from-white to-[#f3eefb]" : ""
              } ${
                highlighted === s.sku ? "ring-2 ring-[#ac62ea] shadow-[0_0_0_8px_rgba(172,98,234,0.18)]" : ""
              }`}
              data-testid={`service-${i}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="label-mono">{s.category}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <h3 className="font-display text-2xl tracking-tight font-semibold" data-testid={`service-title-${i}`}>{s.title}</h3>
                    <button
                      type="button"
                      onClick={() => copyShareLink(s.sku, s.title)}
                      className="text-muted-foreground/60 hover:text-[#ac62ea] transition-colors p-1 -ml-1 rounded"
                      title="Copy share link"
                      aria-label={`Copy share link for ${s.title}`}
                      data-testid={`service-share-link-${i}`}
                    >
                      <Link2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground" data-testid={`price-label-${i}`}>{s.quote_only ? "Pricing" : priceLabel(s.recurring)}</div>
                  <div className="font-display text-3xl mt-1" data-testid={`service-price-${i}`}>
                    {renderPrice(s)}
                    {s.recurring && !s.quote_only && <span className="text-xs text-muted-foreground ml-1">/{s.cadence || "mo"}</span>}
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mt-5 leading-relaxed">{s.description}</p>

              {s.personas && s.personas.length > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid={`personas-${i}`}>
                  <span className="label-mono mr-1">Built for</span>
                  {s.personas.map((p) => (
                    <span
                      key={p}
                      className="px-2 py-0.5 rounded-full text-[11px] font-medium border border-[#ac62ea]/30 bg-[#ac62ea]/[0.06] text-[#7d3fbf] tracking-tight"
                      data-testid={`persona-chip-${p.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {p}
                    </span>
                  ))}
                </div>
              )}

              {s.best_for && (
                <div className="mt-5 flex items-start gap-3 p-3 rounded-lg bg-[#f3eefb] border border-[#ac62ea]/15" data-testid={`best-for-${i}`}>
                  <Target className="h-4 w-4 text-[#ac62ea] mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <span className="label-mono mr-1.5">Best for</span>
                    <span className="text-foreground/85">{s.best_for}</span>
                  </div>
                </div>
              )}

              {s.deliverable && (
                <div className="mt-3 flex items-start gap-3 p-3 rounded-lg bg-muted/60 border border-border" data-testid={`deliverable-${i}`}>
                  <FileText className="h-4 w-4 text-foreground/60 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <span className="label-mono mr-1.5">{s.deliverable_label || "Deliverable"}</span>
                    <span className="text-foreground/85">{s.deliverable}</span>
                  </div>
                </div>
              )}

              {s.category === "session" && s.duration_minutes > 0 && (
                <div className="flex items-center gap-2 mt-5 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" /> {s.duration_minutes} minutes
                </div>
              )}

              {CASE_STUDY_BY_SKU[s.sku] && (
                <div className="mt-5" data-testid={`case-study-link-${s.sku}`}>
                  <Link
                    to={CASE_STUDY_BY_SKU[s.sku].href}
                    className="group inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#ac62ea]/30 bg-[#ac62ea]/[0.06] hover:bg-[#ac62ea]/[0.12] text-sm font-medium text-[#7d3fbf] hover:text-[#5d2ba0] transition-colors"
                  >
                    <span className="label-mono !text-[#7d3fbf] !opacity-100">Proof</span>
                    <span>{CASE_STUDY_BY_SKU[s.sku].label}</span>
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              )}

              <div className="mt-5 text-xs text-muted-foreground" data-testid={`service-terms-link-${s.sku}`}>
                <Link
                  to={termsHrefFor(s)}
                  className="inline-flex items-center gap-1.5 hover:text-[#ac62ea] underline-offset-2 hover:underline"
                >
                  <ScrollText className="h-3 w-3" /> Terms &amp; refund policy
                </Link>
              </div>

              <div className="mt-8 pt-6 border-t border-border flex justify-between items-center gap-3">
                <span className="text-xs text-muted-foreground" data-testid={`intake-tag-${i}`}>{intakeTag(s.sku)}</span>
                <Button
                  asChild
                  className="rounded-full bg-[#ac62ea] hover:bg-[#8a48cf] whitespace-nowrap"
                  data-testid={`book-service-${i}`}
                >
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleBookClick(s)}
                  >
                    {ctaLabel(s.sku)} <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* HIGHLIGHTED Off-menu / custom project callout */}
      <section
        className="mt-24 relative overflow-hidden rounded-2xl bg-ink text-white p-10 sm:p-14"
        data-testid="custom-project-callout"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full opacity-35"
            style={{ background: "radial-gradient(circle, rgba(172,98,234,0.55) 0%, transparent 60%)" }} />
        </div>
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 bg-white/[0.05]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ac62ea]" />
              <span className="text-[11px] text-white/85 font-medium uppercase tracking-widest">Off menu · Custom projects</span>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-semibold mt-6 tracking-[-0.035em] leading-[1.05]">
              Need something off menu?
            </h2>
            <p className="text-white/70 mt-5 leading-relaxed max-w-2xl text-lg">
              For multi-month engagements, custom packages, RFI/RFP support, non-standard retainer terms or anything else outside the catalog — submit a custom project brief and we&apos;ll respond with a tailored scope, quote and payable invoice. Usually within 48 hours.
            </p>
            <p className="text-sm text-white/55 mt-5" data-testid="custom-sales-contact">
              Prefer to talk first?{" "}
              <Link to="/contact" className="text-[#ac62ea] hover:underline font-medium" data-testid="custom-sales-contact-link">talk to us &rarr;</Link>
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-12 px-6 font-semibold" data-testid="custom-project-cta">
              <Link to="/inquiry">Submit a custom project brief <ArrowUpRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
